import { Component, computed, DestroyRef, inject, input, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, Validators, FormGroup, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { GedDocumentType, FileMetadata, SignatureState, EtatRecueil, EtatQpi, EtatDer, DocumentType } from '@lib/models/generated/graphql';
import { ReferenceDataService } from '@lib/services/reference-data.service';
import { OuiAutocompleteOption, OuiSelectOption } from 'omnium-ui/form-field';
import { combineLatest, of, startWith } from 'rxjs';
import { InvestisseurService } from '@lib/services/investisseur.service';
import { DocumentForm } from '../models/document-form.model';
import { SHARED_IMPORTS } from 'projects/minotaure-lib/src/shared-imports';
import { requiredDateErrors, requiredSignatureDateErrors } from '@lib/utils/errors-messages';
import { minDate, minDateValidator } from '@lib/validators/min-date.validator';
import { getLastYearDate, minSignatureDateValidator } from '@lib/validators/min-signature-date.validator';
import { currentDatePivotYear } from '@lib/utils/app-utils';

@Component({
  selector: 'app-add-document-form',
  imports: [SHARED_IMPORTS],
  templateUrl: './add-document-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './add-document-form.component.scss',
})
export class AddDocumentFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  public referenceDataService = inject(ReferenceDataService);
  public investisseurService = inject(InvestisseurService);
  private destroyRef = inject(DestroyRef);
  public isSignatureDocument = input<boolean>(false);
  public excludedDocumentTypeCodes = input<string[]>([]);
  public documentTypes = signal<DocumentType[]>([]);
  public creationFilteredOptions = computed<OuiAutocompleteOption[]>(() =>
    this.documentTypes()
      .filter(docType => !docType.isDeprecated && !this.docTypesToRemove.includes(docType.gedTypeCode) && !this.excludedDocumentTypeCodes().includes(docType.gedTypeCode))
      .map(docType => ({
        label: docType.label,
        value: docType.gedTypeCode,
      }))
  );
  public referenceDateDocumentTypes = computed<ReadonlySet<string>>(
    () => new Set(this.documentTypes().filter(documentType => documentType.hasReferenceDate).map(documentType => documentType.gedTypeCode))
  );
  public acceptedFileType = computed<string>(() =>
    this.isSignatureDocument()
      ? '.pdf'
      : '.pdf, image/*, application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel'
  );
  public acceptedFileLabel = computed<string>(() => (this.isSignatureDocument() ? 'PDF' : 'PDF, JPEG, PNG, Word, Excel'));
  public acceptedFileSize = computed<string>(() => (this.isSignatureDocument() ? '10' : '20'));
  public fileUpdloaderHint = computed<string>(
    () => `Fichiers acceptés : ${this.acceptedFileLabel()} - Poids maximum par document : ${this.acceptedFileSize()} Mo`
  );
  public fileControl = new FormControl<File[]>([]);
  public formArray = this.fb.array<FormGroup<DocumentForm>>([]);
  public requiredSignatureDateTypes = [
    GedDocumentType.RecInfo.toString(),
    GedDocumentType.Der.toString(),
    GedDocumentType.ProfInvest.toString(),
  ];

  public docTypesToRemove = [
    GedDocumentType.Cni.toString(),
    GedDocumentType.Passeport.toString(),
    GedDocumentType.CniHeberg.toString(),
    GedDocumentType.CniMere.toString(),
    GedDocumentType.CniPere.toString(),
  ];

  public investisseurOptions = signal<OuiSelectOption<string>[]>([]);
  public isCouple = signal<boolean>(false);
  public investisseurDocuments = signal<FileMetadata[]>([]);
  public derSignatureInProgress = computed<boolean>(() =>
    this.investisseurDocuments().some(doc => doc.typeDocumentCode === GedDocumentType.Der && doc.etatSignature === SignatureState.Pending)
  );
  public recueilSignatureInProgress = computed<boolean>(() =>
    this.investisseurDocuments().some(
      doc => doc.typeDocumentCode === GedDocumentType.RecInfo && doc.etatSignature === SignatureState.Pending
    )
  );
  public qpiSignatureInProgress = computed<boolean>(() =>
    this.investisseurDocuments().some(
      doc => doc.typeDocumentCode === GedDocumentType.ProfInvest && doc.etatSignature === SignatureState.Pending
    )
  );
  public recueilImcomplete = computed<boolean>(
    () => this.investisseurService.investisseurSig()?.avancementConnaissanceClientInvestisseur?.recueilEtat === EtatRecueil.ACompleter
  );
  public qpiImcomplete = computed<boolean>(
    () => this.investisseurService.investisseurSig()?.avancementConnaissanceClientInvestisseur?.qpiEtat === EtatQpi.ACompleter
  );
  public derIncomplete = computed<boolean>(
    () => this.investisseurService.investisseurSig()?.avancementConnaissanceClientInvestisseur?.derEtat === EtatDer.ACompleter
  );

  public maxSignatureDate = signal<Date>(new Date());
  public minSignatureDate = computed<Date>(() => {
    let date;
    if (this.lastYearSignatureDate()) {
      date = getLastYearDate();
      date.setDate(date.getDate() + 1);
    } else {
      date = minDate;
    }
    return date;
  });
  public lastYearSignatureDate = signal<boolean>(false);
  readonly dateErrors = requiredSignatureDateErrors;
  readonly referenceDateErrors = requiredDateErrors;
  readonly currentDatePivotYear = currentDatePivotYear();

  ngOnInit(): void {
    this.referenceDataService.getDocumentTypes().subscribe(documentTypes => {
      this.documentTypes.set(
        documentTypes.filter(docType => !this.docTypesToRemove.includes(docType.gedTypeCode)) as unknown as DocumentType[]
      );
    });
    const investisseur = this.investisseurService.investisseurSig();
    const conjoint = this.investisseurService.investisseurConjointSig();
    if (conjoint) {
      this.isCouple.set(true);
      this.investisseurOptions.set([
        {
          label: `${investisseur?.personnePhysique[0]?.prenom} ${investisseur?.personnePhysique[0]?.nom}`,
          value: investisseur?.id.toString(),
        },
        {
          label: `${conjoint?.personnePhysique[0]?.prenom} ${conjoint?.personnePhysique[0]?.nom}`,
          value: conjoint?.id.toString(),
        },
        {
          label: 'Couple',
          value: 'couple',
        },
      ]);
    }

    this.fileControl.valueChanges.subscribe(files => {
      this.buildForms(files);
    });

    this.investisseurService.investisseurDocuments$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(docs => {
      this.investisseurDocuments.set(docs ?? []);
    });
  }

  public deleteFile(index: number): void {
    this.formArray.removeAt(index);
    this.fileControl.value?.splice(index, 1);
  }

  private buildForms(files: File[] | null) {
    this.formArray = this.fb.array<FormGroup<DocumentForm>>(
      (files ?? []).map(file => {
        const group = this.fb.group(
          {
            file: this.fb.control(file, { nonNullable: true }),
            documentType: this.fb.control('', { nonNullable: true, validators: [Validators.required] }),
            documentTypeLabel: this.fb.control(''),
            isSigned: this.fb.control(false, { nonNullable: true }),
            signatureDate: this.fb.control(''),
            referenceDate: this.fb.control(''),
            relatedInvestisseur: this.fb.control(this.isCouple() ? 'couple' : this.investisseurService.investisseurSig()?.id.toString()),
            displayBanner: this.fb.control(false, { nonNullable: true }),
            bannerLabel: this.fb.control(''),
          },
          { validators: [this.bannerValidator()] }
        );

        this.subscribeToFormChange(group);
        return group;
      })
    );
  }

  private bannerValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const displayBanner = group.get('displayBanner')?.value;
      if (displayBanner === true) {
        return { bannerDisplayed: true };
      }
      return null;
    };
  }

  private subscribeToFormChange(formGroup: FormGroup<DocumentForm>): void {
    combineLatest([
      formGroup.controls.documentType.valueChanges.pipe(startWith(formGroup.controls.documentType.value)),
      this.isSignatureDocument() ? of(true) : formGroup.controls.isSigned.valueChanges.pipe(startWith(formGroup.controls.isSigned.value)),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([documentType, isSigned]) => {
        const hasReferenceDate = this.documentTypes().find(type => type.gedTypeCode === documentType)?.hasReferenceDate;
        formGroup.controls.referenceDate.setValidators(hasReferenceDate ? [Validators.required, minDateValidator] : null);
        if (!hasReferenceDate) formGroup.controls.referenceDate.patchValue(null);
        formGroup.controls.referenceDate.updateValueAndValidity();

        // Handle docs with signature date requirement
        if (this.requiredSignatureDateTypes.includes(documentType) && isSigned) {
          if (!this.isSignatureDocument()) {
            const validator = [GedDocumentType.RecInfo, GedDocumentType.ProfInvest].includes(documentType as GedDocumentType)
              ? minSignatureDateValidator
              : minDateValidator;
            formGroup.controls.signatureDate.setValidators([Validators.required, validator]);
            formGroup.controls.signatureDate.updateValueAndValidity();
          }

          // Case signature in progress : can't upload another signed document of the same type
          if (
            (this.derSignatureInProgress() && documentType === GedDocumentType.Der) ||
            (this.recueilSignatureInProgress() && documentType === GedDocumentType.RecInfo) ||
            (this.qpiSignatureInProgress() && documentType === GedDocumentType.ProfInvest)
          ) {
            formGroup.controls.displayBanner.patchValue(true);
            formGroup.controls.bannerLabel.patchValue(
              `Un document de même nature est actuellement en cours de signature. Pour ajouter ce document, vous devez terminer le processus de signature en cours ou l’annuler.`
            );
          }

          // Case recueil incomplete
          if (this.recueilImcomplete() && documentType === GedDocumentType.RecInfo) {
            formGroup.controls.displayBanner.patchValue(true);
            formGroup.controls.bannerLabel.patchValue(
              `Vous ne pouvez pas ajouter un recueil d’informations signé tant que le formulaire « Recueil d’informations » de la connaissance client n’a pas été entièrement complété.`
            );
          }

          // Case QPI incomplete
          if (this.qpiImcomplete() && documentType === GedDocumentType.ProfInvest) {
            formGroup.controls.displayBanner.patchValue(true);
            formGroup.controls.bannerLabel.patchValue(
              `Vous ne pouvez pas ajouter un Profil Investisseur signé tant que le formulaire « Profil Investisseur » de la connaissance client n’a pas été entièrement complété.`
            );
          }

          // Case DER incomplete
          if (this.derIncomplete() && documentType === GedDocumentType.Der) {
            formGroup.controls.displayBanner.patchValue(true);
            formGroup.controls.bannerLabel.patchValue(
              `Vous ne pouvez pas ajouter un Document d'entrée en relation signé tant que vos données nécessaires ne sont pas remplies dans MyProdemial.`
            );
          }

          // Case DER is not ready to sign : can't upload Recueil or QPI
          if ([GedDocumentType.RecInfo, GedDocumentType.ProfInvest].includes(documentType as GedDocumentType) && this.derIncomplete()) {
            formGroup.controls.displayBanner.patchValue(true);
            formGroup.controls.bannerLabel.patchValue(
              `Le DER doit être préalablement complété avant de signer le ${documentType === GedDocumentType.RecInfo ? "recueil d'informations" : 'profil investisseur'} `
            );
          }
          // Case Recueil is not ready to sign : can't upload QPI
          if ((documentType as GedDocumentType) === GedDocumentType.ProfInvest && this.recueilImcomplete()) {
            formGroup.controls.displayBanner.patchValue(true);
            formGroup.controls.bannerLabel.patchValue(
              `Le recueil d'informations doit être préalablement complété avant de signer le profil investisseur`
            );
          }
        } else {
          formGroup.controls.signatureDate.patchValue(null);
          formGroup.controls.signatureDate.clearValidators();
          formGroup.controls.signatureDate.updateValueAndValidity();
          formGroup.controls.displayBanner.patchValue(false);
          formGroup.controls.bannerLabel.patchValue('');
        }

        this.lastYearSignatureDate.set([GedDocumentType.RecInfo, GedDocumentType.ProfInvest].includes(documentType as GedDocumentType));

        formGroup.controls.documentTypeLabel.patchValue(this.getDocumentLabel(formGroup.controls.documentType.value));
      });
  }

  private getDocumentLabel(documentTypeCode: string): string {
    return this.documentTypes().find(dt => dt.gedTypeCode === documentTypeCode)?.label ?? documentTypeCode;
  }
}
